import { InfoCircleOutlined, MessageOutlined } from "@ant-design/icons";
import { Empty, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerDetailsModal from "./CustomerDetailsModal";
import { useTranslation } from "react-i18next";
import { CustomerConnection } from "../../../api/ChannelCustomer";
import { CHANNEL_TYPE_LABELS, getChannelImage } from "../../chats/channelTypes";

interface Props {
    connections?: CustomerConnection[];
}

// Kênh của khách nằm trong bảng nối customers <-> channel_customers.
// Mỗi bản ghi mang sẵn quan hệ channel_connection (trang) và channel_customer (khách trên trang đó).
export default function Channels({ connections }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selected, setSelected] = useState<CustomerConnection | null>(null);

    const items = connections || [];

    if (items.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <span className="text-gray-500 dark:text-gray-400">{t('cd.channel_empty')}</span>
                }
            />
        );
    }

    // Cùng thứ tự dự phòng với nút "Xem cuộc trò chuyện" ở đầu trang
    const getConnectionId = (c: CustomerConnection) =>
        c.channel_customer?.channel_connection_id ??
        c.channel_connection_id ??
        c.channel_connection?.id ??
        null;

    const formatDate = (raw?: string) => {
        if (!raw) return null;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString();
    };

    return (
        <>
            <div className="flex flex-col gap-3">
                {items.map((conn) => {
                    const typeCode = conn.channel_connection?.channel_type_code || conn.source || "";
                    const channelName = conn.channel_customer?.display_name || t('cd.unknown');
                    const pageName = conn.channel_connection?.display_name;
                    const connectionId = getConnectionId(conn);
                    const canOpenChat = !!connectionId && !!conn.channel_customers_id;
                    const linkedAt = formatDate(conn.created_at);

                    return (
                        <div
                            key={conn.id}
                            className="
                                border border-gray-200
                                rounded-lg
                                p-4
                                flex justify-between gap-3
                                bg-white
                                dark:bg-[#141414]
                                dark:border-[#303030]
                            "
                        >
                            <div className="flex flex-col gap-1 min-w-0">
                                {/* LEFT */}
                                <div className="flex gap-2 items-center min-w-0">
                                    {/* Avatar */}
                                    <div
                                        className="
                                            w-[44px] h-[44px]
                                            rounded-full
                                            bg-gray-300
                                            dark:bg-[#2a2a2a]
                                            flex items-center justify-center
                                            text-white text-lg font-semibold
                                            flex-shrink-0
                                        "
                                    >
                                        {channelName.charAt(0).toUpperCase()}
                                    </div>

                                    {/* Info */}
                                    <div className="min-w-0">
                                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                            {channelName}
                                        </div>

                                        <div className="text-gray-500 dark:text-gray-400 text-sm">
                                            {t('cd.channel_customer_id')} {conn.channel_customers_id}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-sm text-gray-700 text-000000e0">
                                        {t('cd.page_name')}{" "}
                                        {pageName ? (
                                            <span className="text-gray-800 text-000000e0">{pageName}</span>
                                        ) : (
                                            <span className="italic text-gray-500 dark:text-gray-400">
                                                {t('cd.unknown')}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-1 text-sm text-gray-700 text-000000e0 flex items-center gap-2">
                                        {t('cd.source')}
                                        {typeCode ? (
                                            <>
                                                <img
                                                    src={getChannelImage(typeCode)}
                                                    alt={typeCode}
                                                    className="w-5 h-5 rounded-sm object-cover"
                                                />
                                                <span>{CHANNEL_TYPE_LABELS[typeCode] || typeCode}</span>
                                            </>
                                        ) : (
                                            <span className="italic text-gray-500 dark:text-gray-400">
                                                {t('cd.unknown')}
                                            </span>
                                        )}
                                    </div>

                                    {linkedAt && (
                                        <div className="mt-1 text-sm text-gray-700 text-000000e0">
                                            {t('cd.channel_linked_at')} {linkedAt}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT ICONS */}
                            <div className="flex gap-2 flex-shrink-0">
                                <Tooltip title={t('cd.channel_info')}>
                                    <button
                                        onClick={() => setSelected(conn)}
                                        className="
                                                w-9 h-9
                                                border border-gray-300
                                                dark:border-[#303030]
                                                rounded-md
                                                flex items-center justify-center
                                                text-gray-600
                                                text-000000e0
                                                hover:bg-gray-100
                                                dark:hover:bg-[#1f1f1f]
                                            "
                                    >
                                        <InfoCircleOutlined />
                                    </button>
                                </Tooltip>

                                <Tooltip title={t('cd.open_conversation')}>
                                    <button
                                        disabled={!canOpenChat}
                                        onClick={() => navigate(`/chat/${connectionId}?customer_id=${conn.channel_customers_id}`)}
                                        className="
                                                w-9 h-9
                                                border border-gray-300
                                                dark:border-[#303030]
                                                rounded-md
                                                flex items-center justify-center
                                                text-gray-600
                                                text-000000e0
                                                hover:bg-gray-100
                                                dark:hover:bg-[#1f1f1f]
                                                disabled:opacity-40
                                                disabled:cursor-not-allowed
                                                disabled:hover:bg-transparent
                                            "
                                    >
                                        <MessageOutlined />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal */}
            <CustomerDetailsModal
                open={!!selected}
                connection={selected}
                onClose={() => setSelected(null)}
            />
        </>
    );
}
